import { clearFCMTokenFromBackend } from '@/services/firebase'

// Prevent re-entry if kick is already in progress
let kickInProgress = false

/**
 * Dispatch a global event that signals the owner's subscription has expired.
 */
export function dispatchAgentSubscriptionExpired () {
  window.dispatchEvent(new CustomEvent('agent-subscription-expired'))
}

/**
 * Dispatch a global event that signals the agent's session was revoked.
 * Called from anywhere a 401 is detected while an agent_token exists.
 */
export function dispatchAgentKick () {
  window.dispatchEvent(new CustomEvent('agent-session-kicked'))
}

/**
 * Full cleanup + redirect for a kicked agent.
 * Navigates FIRST (while token still exists so the router guard doesn't interfere),
 * then clears FCM token and localStorage after navigation settles.
 * @param {import('vue-router').Router} router
 */
export async function kickAgent (router) {
  if (kickInProgress) return
  kickInProgress = true

  try {
    // Navigate first — token still in localStorage so the guard passes cleanly
    await router.replace('/agent/kicked')
  } catch (_) {
    // NavigationDuplicated or similar — already on kicked screen
  } finally {
    // Clear FCM token from backend (best-effort, non-blocking)
    const agentToken = localStorage.getItem('agent_token')
    if (agentToken) clearFCMTokenFromBackend(agentToken).catch(() => {})

    // Wipe local auth state
    localStorage.removeItem('agent_token')
    localStorage.removeItem('agent_data')
    localStorage.removeItem('auth_token')

    kickInProgress = false
  }
}

/**
 * Dispatch a global event that signals the agent's permissions have been refreshed in localStorage.
 * Components listening for this event should re-read agent_data from localStorage.
 */
export function dispatchAgentPermissionsUpdated () {
  window.dispatchEvent(new CustomEvent('agent-permissions-updated'))
}

/**
 * Poll GET /agents/my-permissions every 30 seconds + on tab visibility change.
 * When permissions differ from what's stored in agent_data, updates localStorage
 * and dispatches 'agent-permissions-updated' so UI components can react without a logout.
 *
 * @returns {Function} Cleanup function — call it on component unmount.
 */
export function startAgentPermissionsPolling () {
  const poll = async () => {
    const token = localStorage.getItem('agent_token')
    if (!token) return

    try {
      const res = await fetch('https://api.quali-d.com/agents/my-permissions', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        }
      })

      if (!res.ok) return

      const data = await res.json()
      if (!data.success) return

      const agentData = JSON.parse(localStorage.getItem('agent_data') || '{}')
      const p = data.permissions
      let changed = false

      if (agentData.allow_broadcast !== p.allow_broadcast) {
        agentData.allow_broadcast = p.allow_broadcast
        changed = true
      }
      if (agentData.show_customer_phone !== p.show_customer_phone) {
        agentData.show_customer_phone = p.show_customer_phone
        changed = true
      }

      if (changed) {
        localStorage.setItem('agent_data', JSON.stringify(agentData))
        dispatchAgentPermissionsUpdated()
      }
    } catch (_) {
      // Network error — skip this cycle
    }
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') poll()
  }
  document.addEventListener('visibilitychange', onVisibilityChange)

  // Run once immediately so the UI reflects current server state without waiting 30 s
  poll()

  const interval = setInterval(poll, 30000)

  return () => {
    clearInterval(interval)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}

/**
 * Poll the subscription status endpoint every 30 seconds while an agent is active.
 * If the owner's subscription has expired, dispatches agent-subscription-expired.
 *
 * @returns {Function} Cleanup function — call it on component unmount.
 */
export function startAgentSubscriptionPolling () {
  const poll = async () => {
    const token = localStorage.getItem('agent_token')
    if (!token) return

    try {
      const res = await fetch('https://api.quali-d.com/payments/subscription-status', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        }
      })

      if (!res.ok) return // Network/server error — skip, don't kick on connectivity issues

      const data = await res.json()
      if (data.success === false) {
        dispatchAgentSubscriptionExpired()
      }
    } catch (_) {
      // Network error — skip this cycle
    }
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') poll()
  }
  document.addEventListener('visibilitychange', onVisibilityChange)

  const interval = setInterval(poll, 30000)

  return () => {
    clearInterval(interval)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}
